import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import RelatorioApiService from '../../../services/api/RelatorioApiService';
import FormatDataService from '../../../services/util/FormatData';
import { MdOutlineArrowBack, MdArrowUpward, MdArrowDownward } from 'react-icons/md';
import PageContainer from '../../../components/PageContainer';
import Loading from "../../../components/Loading";
import { Button, Table, Alert, Dropdown } from 'react-bootstrap';
import UtilService from '../../../services/util';
import useAlertNotification from '../../../context/AlertNotificationContext';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

/**
 * Formata horas em formato HH:MM
 * @param {string|number} hours - Horas em formato string HH:MM ou decimal
 * @returns {string} Horas formatadas no formato HH:MM
*/
const formatHoursAndMinutes = (hours) => {
    if (hours === null || hours === undefined) return "0:00";

    // Se já está no formato HH:MM, retorna como está
    if (typeof hours === 'string' && hours.includes(':')) {
        return hours;
    }

    // Se é um número decimal, converte para HH:MM
    if (typeof hours === 'number') {
        const sign = hours < 0 ? "-" : "";
        const absHours = Math.abs(hours);
        const h = Math.floor(absHours);
        const m = Math.round((absHours - h) * 60);
        const formattedMinutes = m.toString().padStart(2, '0');

        return `${sign}${h}:${formattedMinutes}`;
    }

    return "0:00";
};

/**
 * Formata horas em formato HH:MM para exportação
 * @param {string|number} hours - Horas em formato string HH:MM ou decimal
 * @returns {string} Horas no formato HH:MM
*/
const formatHoursForExport = (hours) => {
    return formatHoursAndMinutes(hours);
};

/**
 * Converte horas (HH:MM ou número) para valor numérico em horas
 * @param {string|number} hours - Horas em formato string HH:MM ou decimal
 * @returns {number} Horas como número decimal
*/
const hoursToNumber = (hours) => {
    if (hours === null || hours === undefined) return 0;

    // Se já é número, retorna com sinal preservado
    if (typeof hours === 'number') {
        return hours;
    }

    // Se string no formato HH:MM (com ou sem sinal)
    if (typeof hours === 'string' && hours.includes(':')) {
        const trimmed = hours.trim();
        const isNegative = trimmed.startsWith('-');

        // Remove o sinal para parse
        const clean = trimmed.replace('-', '');
        const parts = clean.split(':');

        if (parts.length === 2) {
            const h = parseInt(parts[0], 10) || 0;
            const m = parseInt(parts[1], 10) || 0;

            const decimal = h + m / 60;

            // Reaplica o sinal corretamente
            return isNegative ? -decimal : decimal;
        }
    }

    return 0;
};


export default function RelatorioPontoJiraView() {
    const location = useLocation();
    const history = useHistory();
    const alertNotification = useAlertNotification();
    const { data } = location.state || { data: { periodoInicial: "", periodoFinal: "" } };
    const { periodoInicial, periodoFinal } = data;

    const [pontoJiraData, setPontoJiraData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [selectedRegime, setSelectedRegime] = useState('');
    const notificationShownRef = useRef(false);
    const previousFiltersRef = useRef('');

    const fetchPontoJiraData = useCallback(async () => {
        if (!periodoInicial || !periodoFinal) {
            setError('Período inicial e final são obrigatórios');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const dataInicialFormatada = FormatDataService.formatarDataParaYYYYMMDD(periodoInicial);
            const dataFinalFormatada = FormatDataService.formatarDataParaYYYYMMDD(periodoFinal);
            const response = await RelatorioApiService.getRelatorioPontoJira(dataInicialFormatada, dataFinalFormatada);

            const newData = response.data.data || [];
            setPontoJiraData(newData);
            // Reset da flag de notificação quando novos dados são carregados
            notificationShownRef.current = false;
            previousFiltersRef.current = '';
        } catch (error) {
            console.error('Erro ao carregar dados do relatório:', error);
            setError('Erro ao carregar dados do relatório. Tente novamente.');
        } finally {
            setLoading(false);
        }
    }, [periodoInicial, periodoFinal]);

    useEffect(() => {
        fetchPontoJiraData();
    }, [fetchPontoJiraData]);

    // Notificação quando há filtros selecionados mas não há resultados
    useEffect(() => {
        const hasFilters = selectedDepartment || selectedRegime;
        const currentFilters = `${selectedDepartment}-${selectedRegime}`;

        // Reset da flag quando os filtros mudarem
        if (previousFiltersRef.current !== currentFilters) {
            notificationShownRef.current = false;
            previousFiltersRef.current = currentFilters;
        }

        // Mostra notificação quando há filtros selecionados mas não há resultados filtrados
        if (hasFilters && filteredData.length === 0 && pontoJiraData.length > 0 && !loading && !notificationShownRef.current) {
            alertNotification.info('Nenhum dado encontrado com os filtros selecionados.');
            notificationShownRef.current = true;
        }
    }, [selectedDepartment, selectedRegime, filteredData, pontoJiraData, loading, alertNotification]);

    // Filtros por departamento e regime
    useEffect(() => {
        // Se não há filtros selecionados, mostra todos os dados
        if (!selectedDepartment && !selectedRegime) {
            setFilteredData(pontoJiraData);
            return;
        }

        const filtered = pontoJiraData.filter((item) => {
            console.log(item);
            // Verifica se o item tem estrutura aninhada (colaborador.departamento) ou direta (departamento)
            const departamento = item.departamento;
            const regime = item.regime;

            // Normaliza os valores para comparação
            const departamentoNormalizado = typeof departamento === 'string' ? departamento : '';
            const regimeNormalizado = typeof regime === 'string' ? regime : '';

            // Filtro por departamento
            const matchesDepartment = selectedDepartment
                ? departamentoNormalizado.toLowerCase() === selectedDepartment.toLowerCase() ||
                (selectedDepartment === 'Delivery' && (departamentoNormalizado.toLowerCase().includes('delivery'))) ||
                (selectedDepartment === 'ENGESOL' && (departamentoNormalizado.toLowerCase().includes('engesol'))) ||
                (selectedDepartment === 'Overhead' && (departamentoNormalizado.toLowerCase().includes('overhead')))
                : true;

            // Filtro por regime
            const matchesRegime = selectedRegime
                ? regimeNormalizado.toLowerCase() === selectedRegime.toLowerCase() ||
                (selectedRegime === 'CLT' && (regimeNormalizado.toLowerCase().includes('clt'))) ||
                (selectedRegime === 'Estagiário' && (regimeNormalizado.toLowerCase().includes('estagiário')))
                : true;

            return matchesDepartment && matchesRegime;
        });
        setFilteredData(filtered);
    }, [selectedDepartment, selectedRegime, pontoJiraData]);

    /**
     * Converte string de tempo HH:MM para minutos para ordenação
     * @param {string} timeString - String no formato HH:MM
     * @returns {number} Total de minutos
     */
    const timeStringToMinutes = (timeString) => {
        if (!timeString || typeof timeString !== 'string') return 0;

        const parts = timeString.split(':');
        if (parts.length !== 2) return 0;

        const hours = parseInt(parts[0], 10) || 0;
        const minutes = parseInt(parts[1], 10) || 0;

        return hours * 60 + minutes;
    };

    /**
     * Função para ordenar os dados
     * @param {string} key - Chave da coluna para ordenar
     */
    const handleSort = useCallback((key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    }, [sortConfig]);

    /**
     * Dados ordenados baseados na configuração de ordenação
     */
    const sortedData = useMemo(() => {
        // Se há filtros selecionados, sempre usa filteredData (mesmo que vazio)
        // Se não há filtros, usa todos os dados
        const hasFilters = selectedDepartment || selectedRegime;
        const dataToSort = hasFilters ? filteredData : (filteredData.length > 0 ? filteredData : pontoJiraData);
        if (!sortConfig.key) return dataToSort;

        return [...dataToSort].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            // Para colunas de tempo, converte para minutos para ordenação
            if (sortConfig.key.includes('horas') || sortConfig.key === 'diferenca') {
                aValue = timeStringToMinutes(aValue);
                bValue = timeStringToMinutes(bValue);
            }

            // Para strings, converte para minúsculo para ordenação case-insensitive
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [filteredData, pontoJiraData, sortConfig, selectedDepartment, selectedRegime]);

    const exportDataInExcel = useCallback(async () => {
        if (sortedData.length === 0) {
            setError('Não há dados para exportar');
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Relatório Ponto vs Jira');

        // Cabeçalho
        const headerRow = worksheet.addRow(['Colaborador', 'Ponto Two', 'Jira', 'Diferença', 'Gestor']);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF4F81BD' } // Azul
            };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = {
                top: { style: 'thin', color: { argb: 'FF000000' } },
                left: { style: 'thin', color: { argb: 'FF000000' } },
                bottom: { style: 'thin', color: { argb: 'FF000000' } },
                right: { style: 'thin', color: { argb: 'FF000000' } },
            };
        });

        // Linhas de dados
        sortedData.forEach(item => {
            const row = worksheet.addRow([
                item.nomeCompleto || '',
                formatHoursForExport(item.horasRealizadasPonto),
                formatHoursForExport(item.horasRealizadasJira),
                formatHoursForExport(item.diferenca),
                item.gestor || ''
            ]);

            const horasJira = hoursToNumber(item.horasRealizadasJira);
            const horasPonto = hoursToNumber(item.horasRealizadasPonto);
            const diferenca = hoursToNumber(item.diferenca);

            // Define a cor com base nas regras
            let fillColor = 'FF00B050'; // verde (padrão)
            if (horasJira === 0 || horasPonto === 0) {
                fillColor = 'FFFF0000'; // vermelho tem prioridade
            } else if (diferenca >= 2 || diferenca <= -2) {
                fillColor = 'FFFFFF00'; // amarelo
            }

            // Aplicar bordas e alinhamento em toda a linha
            row.eachCell((cell) => {
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFAAAAAA' } },
                    left: { style: 'thin', color: { argb: 'FFAAAAAA' } },
                    bottom: { style: 'thin', color: { argb: 'FFAAAAAA' } },
                    right: { style: 'thin', color: { argb: 'FFAAAAAA' } },
                };
            });

            // Colorir apenas colunas Ponto Two (2), Jira (3) e Diferença (4)
            [2, 3, 4].forEach(index => {
                const cell = row.getCell(index);
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: fillColor }
                };
            });
        });

        // Ajustar largura automática
        worksheet.columns.forEach((col) => {
            let maxLength = 0;
            col.eachCell({ includeEmpty: true }, cell => {
                const val = cell.value ? cell.value.toString().length : 10;
                if (val > maxLength) maxLength = val;
            });
            col.width = Math.max(15, maxLength + 2);
        });

        // Gera e baixa o arquivo
        const buffer = await workbook.xlsx.writeBuffer();
        const nomeArquivo = `Relatório Ponto vs Jira - ${new Date(periodoInicial).toLocaleDateString('pt-BR')} a ${new Date(periodoFinal).toLocaleDateString('pt-BR')}.xlsx`;
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), nomeArquivo);
    }, [sortedData, periodoInicial, periodoFinal]);

    const actions = useMemo(
        () => [
            {
                label: "Voltar",
                icon: <MdOutlineArrowBack />,
                color: "info",
                type: "button",
                onClick: () => history.goBack()
            }
        ],
        [history]
    );

    const title = useMemo(() =>
        `Relatório Comparativo Ponto vs Jira - ${new Date(periodoInicial).toLocaleDateString('pt-BR')} a ${new Date(periodoFinal).toLocaleDateString('pt-BR')}`,
        [periodoInicial, periodoFinal]
    );

    const tableRows = useMemo(() =>
        sortedData.map((item, index) => {
            // Converte valores para números para comparação
            const horasJira = hoursToNumber(item.horasRealizadasJira);
            const horasPonto = hoursToNumber(item.horasRealizadasPonto);
            const diferenca = hoursToNumber(item.diferenca);

            // Determina o estilo da linha
            let rowStyle = {};

            // Prioridade: se horasRealizadasJira é 0, linha vermelha
            if (horasJira === 0 || horasPonto === 0) {
                rowStyle.backgroundColor = '#ffcccc'; // Vermelho mais vibrante
            }
            // Se diferença >= 2 (positivo ou negativo), linha amarela
            else if (diferenca >= 2 || diferenca <= -2) {
                rowStyle.backgroundColor = '#fff4cc'; // Amarelo mais vibrante
            }

            return (
                <tr key={item.id || `item-${index}`} style={rowStyle} >
                    <td>{item.nomeCompleto || '-'}</td>
                    <td>{item.gestor || '-'}</td>
                    <td style={rowStyle}>{formatHoursAndMinutes(item.horasRealizadasPonto)}</td>
                    <td style={rowStyle}>{formatHoursAndMinutes(item.horasRealizadasJira)}</td>
                    <td style={rowStyle}>{formatHoursAndMinutes(item.diferenca)}</td>
                </tr>
            );
        }),
        [sortedData]
    );

    return (
        <PageContainer title={title} actions={actions}>
            {loading ? (
                <Loading message="Carregando dados..." />
            ) : (
                <>
                    {error && (
                        <Alert variant="danger" className="mb-3">
                            {error}
                        </Alert>
                    )}

                    {!error && pontoJiraData.length === 0 && (
                        <Alert variant="warning">
                            Nenhum dado disponível para o período selecionado.
                        </Alert>
                    )}

                    {!error && pontoJiraData.length > 0 && (
                        <>
                            <div className="d-flex mb-3">
                                <Button
                                    onClick={exportDataInExcel}
                                    className="mr-3"
                                    disabled={loading || sortedData.length === 0}
                                >
                                    Exportar
                                </Button>
                                <Dropdown className="mr-3">
                                    <Dropdown.Toggle variant="light" id="dd-department">
                                        Filtrar por Departamento: <span style={{ fontWeight: 'bold' }}>{selectedDepartment ? selectedDepartment : "Todos"}</span>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={() => setSelectedDepartment('Delivery')}>
                                            Delivery
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => setSelectedDepartment('ENGESOL')}>
                                            ENGESOL
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => setSelectedDepartment('Overhead')}>
                                            Overhead
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => setSelectedDepartment('')}>
                                            Todos
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                                <Dropdown>
                                    <Dropdown.Toggle variant="light" id="dd-regime">
                                        Filtrar por Regime: <span style={{ fontWeight: 'bold' }}>{selectedRegime ? selectedRegime : "Todos"}</span>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={() => setSelectedRegime('CLT')}>
                                            CLT
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => setSelectedRegime('Estagiário')}>
                                            Estagiário
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => setSelectedRegime('')}>
                                            Todos
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>

                            {/* Mostra mensagem quando há filtros mas não há resultados */}
                            {(selectedDepartment || selectedRegime) && sortedData.length === 0 && (
                                <Alert variant="info">
                                    Nenhum dado encontrado com os filtros selecionados.
                                </Alert>
                            )}

                            {/* Mostra tabela apenas quando há resultados */}
                            {sortedData.length > 0 && (
                                <div className="table-responsive container-fluid p-0">
                                    <Table
                                        striped
                                        bordered
                                        hover
                                        responsive
                                        aria-label="Relatório comparativo Ponto vs Jira"
                                    >
                                        <thead>
                                            <tr>
                                                <th
                                                    scope="col"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSort('nomeCompleto')}
                                                >
                                                    Nome
                                                    {sortConfig.key === 'nomeCompleto' && (
                                                        sortConfig.direction === 'asc' ?
                                                            ' ▴' : ' ▾'
                                                    )}
                                                </th>
                                                <th
                                                    scope="col"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSort('gestor')}
                                                >
                                                    Gestor no Ponto Two
                                                    {sortConfig.key === 'gestor' && (
                                                        sortConfig.direction === 'asc' ?
                                                            ' ▴' : ' ▾'
                                                    )}
                                                </th>
                                                <th
                                                    scope="col"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSort('horasRealizadasPonto')}
                                                >
                                                    Horas Realizadas Ponto
                                                    {sortConfig.key === 'horasRealizadasPonto' && (
                                                        sortConfig.direction === 'asc' ?
                                                            ' ▴' : ' ▾'
                                                    )}
                                                </th>
                                                <th
                                                    scope="col"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSort('horasRealizadasJira')}
                                                >
                                                    Horas Realizadas Jira
                                                    {sortConfig.key === 'horasRealizadasJira' && (
                                                        sortConfig.direction === 'asc' ?
                                                            ' ▴' : ' ▾'
                                                    )}
                                                </th>
                                                <th
                                                    scope="col"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSort('diferenca')}
                                                >
                                                    Diferença
                                                    {sortConfig.key === 'diferenca' && (
                                                        sortConfig.direction === 'asc' ?
                                                            ' ▴' : ' ▾'
                                                    )}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tableRows}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </PageContainer>
    );
};